import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Book, Code, Copy, ExternalLink, Loader2 } from 'lucide-react'
import Editor from '@monaco-editor/react'
import { useAPIRequestStore } from '../../stores/apiStore'
import { APIService } from '../../services/apiService'
import { useTheme } from '../../hooks/useTheme'
import { useToast } from '../ui/ToastProvider'
import ErrorBoundary from '../ErrorBoundary'

const DocumentationPanel: React.FC = () => {
  const { selectedEndpoint, baseUrl, headers, requestBody, queryParams } = useAPIRequestStore()
  const [activeTab, setActiveTab] = useState<'docs' | 'code'>('docs')
  const [selectedLanguage, setSelectedLanguage] = useState('javascript')
  const [isDocsLoading, setIsDocsLoading] = useState(true)
  const [loadingTimeout, setLoadingTimeout] = useState<number | null>(null)
  const isDark = useTheme()
  const { showSuccess, showError } = useToast()

  const apiService = APIService.getInstance()

  const languages = [
    { id: 'javascript', name: 'JavaScript (Axios)' },
    { id: 'python', name: 'Python (requests)' },
    { id: 'php', name: 'PHP (cURL)' },
    { id: 'nodejs', name: 'Node.js' },
    { id: 'flutter', name: 'Flutter (Dart)' },
    { id: 'kotlin', name: 'Kotlin (Android)' },
    { id: 'swift', name: 'Swift (iOS)' },
    { id: 'react-native', name: 'React Native' }
  ]

  // Get contextual documentation URL based on selected endpoint
  const getDocumentationUrl = () => {
    if (!selectedEndpoint) {
      return 'https://docs.momentscience.com/'
    }

    // Use MomentScience documentation URLs for better performance and accuracy
    const endpointDocUrls: Record<string, string> = {
      'offers': 'https://docs.momentscience.com/momentperks-api',
      'perkswall': 'https://docs.momentscience.com/perkswall-api',
      'catalog': 'https://docs.momentscience.com/directperks-api',
      'reporting': 'https://docs.momentscience.com/reporting-api',
    }

    return endpointDocUrls[selectedEndpoint.id] || 'https://docs.momentscience.com/'
  }

  // Reset loading state when endpoint changes
  useEffect(() => {
    setIsDocsLoading(true)
    // Remove error state references
    // setDocsLoadError(false)
    
    // Clear existing timeout
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
    
    // Set timeout for loading state (10 seconds for better UX)
    const timeout = setTimeout(() => {
      setIsDocsLoading(false)
      // Remove error state - just stop loading
      // setDocsLoadError(true)
    }, 10000) as unknown as number
    
    setLoadingTimeout(timeout)
    
    // Cleanup on unmount
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [selectedEndpoint])

  const handleIframeLoad = () => {
    setIsDocsLoading(false)
    // Remove error state references
    // setDocsLoadError(false)
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      setLoadingTimeout(null)
    }
  }

  const handleIframeError = () => {
    setIsDocsLoading(false)
    // Remove error state - just stop loading
    // setDocsLoadError(true)
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      setLoadingTimeout(null)
    }
  }

  const handleCopyCurl = async () => {
    try {
      if (!selectedEndpoint) {
        showError('Please select an endpoint first')
        return
      }
      
      const request = {
        endpoint: selectedEndpoint,
        baseUrl,
        headers,
        requestBody,
        queryParams
      }
      
      const curl = apiService.generateCurl(request, { verbose: true, includeHeaders: true, pretty: true })
      await navigator.clipboard.writeText(curl)
      showSuccess('cURL command copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy cURL to clipboard:', error)
      showError('Failed to copy cURL command')
      // Fallback: show the cURL in a temporary text area for manual copy
      try {
        const request = {
          endpoint: selectedEndpoint!,
          baseUrl,
          headers,
          requestBody,
          queryParams
        }
        const curl = apiService.generateCurl(request, { verbose: true, includeHeaders: true, pretty: true })
        const textArea = document.createElement('textarea')
        textArea.value = curl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        showSuccess('cURL command copied to clipboard!')
      } catch (fallbackError) {
        showError('Unable to copy cURL command')
      }
    }
  }

  const handleCopyCode = async () => {
    try {
      if (!selectedEndpoint) {
        showError('Please select an endpoint first')
        return
      }
      
      const request = {
        endpoint: selectedEndpoint,
        baseUrl,
        headers,
        requestBody,
        queryParams
      }
      
      const code = apiService.generateCodeExample(request, selectedLanguage)
      await navigator.clipboard.writeText(code)
      showSuccess(`${selectedLanguage} code copied to clipboard!`)
    } catch (error) {
      console.error('Failed to copy code to clipboard:', error)
      showError('Failed to copy code example')
      // Fallback: show the code in a temporary text area for manual copy
      try {
        const request = {
          endpoint: selectedEndpoint!,
          baseUrl,
          headers,
          requestBody,
          queryParams
        }
        const code = apiService.generateCodeExample(request, selectedLanguage)
        const textArea = document.createElement('textarea')
        textArea.value = code
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        showSuccess(`${selectedLanguage} code copied to clipboard!`)
      } catch (fallbackError) {
        showError('Unable to copy code example')
      }
    }
  }

  const renderDocumentation = () => {
    const docUrl = getDocumentationUrl()
    
    return (
      <div className="h-full flex flex-col">
        {/* Documentation Header - Compact */}
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {selectedEndpoint ? `${selectedEndpoint.name} Documentation` : 'MomentScience API Documentation'}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {selectedEndpoint ? 'Detailed documentation for this endpoint' : 'Complete API reference and guides'}
            </p>
          </div>
          <a
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-1 bg-[#FE4215] hover:bg-[#E5350A] text-white px-2 py-1.5 rounded text-xs font-medium transition-colors flex-shrink-0"
            title="Open in new tab"
          >
            <ExternalLink className="w-3 h-3" />
            <span>Open</span>
          </a>
        </div>

        {/* Embedded Documentation - Full Height */}
        <div className="flex-1 border border-gray-200 dark:border-gray-700 rounded overflow-hidden bg-white dark:bg-gray-800 min-h-0 relative">
          {/* Loading Indicator */}
          {isDocsLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 z-10">
              <div className="flex flex-col items-center space-y-3">
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading documentation...</p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Connecting to {selectedEndpoint ? 'API documentation' : 'MomentScience Docs'}
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span>Usually takes 3-5 seconds</span>
                </div>
              </div>
            </div>
          )}

          {/* Error State - Removed to prevent showing error message */}
          
          <iframe
            src={docUrl}
            className="w-full h-full border-0"
            title={selectedEndpoint ? `${selectedEndpoint.name} Documentation` : 'MomentScience API Documentation'}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            loading="lazy"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </div>
    )
  }

  const renderCodeAndCurl = () => {
    if (!selectedEndpoint) {
      return (
        <div className="text-center py-8">
          <Code className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Configure a request to generate code examples and cURL command
          </p>
        </div>
      )
    }

    const request = {
      endpoint: selectedEndpoint,
      baseUrl,
      headers,
      requestBody,
      queryParams
    }

    const curl = apiService.generateCurl(request, { verbose: true, includeHeaders: true, pretty: true })
    const code = apiService.generateCodeExample(request, selectedLanguage)

    return (
      <div className="space-y-6">
        {/* cURL Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              cURL Command
            </h3>
            <button
              onClick={handleCopyCurl}
              className="flex items-center space-x-2 px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </button>
          </div>
          
          <div className="h-32 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <ErrorBoundary fallback={
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>Error loading cURL editor. Please try again.</p>
              </div>
            }>
              <Editor
                height="100%"
                language="shell"
                theme={isDark ? "vs-dark" : "light"}
                value={curl}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  readOnly: true,
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  folding: true,
                  bracketPairColorization: { enabled: true }
                }}
              />
            </ErrorBoundary>
          </div>
        </div>

        {/* Code Examples Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Code Examples
            </h3>
            <button
              onClick={handleCopyCode}
              className="flex items-center space-x-2 px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </button>
          </div>

          {/* Language Selector */}
          <div className="flex flex-wrap gap-2">
            {languages.map((lang) => (
              <button
                key={lang.id}
                onClick={() => setSelectedLanguage(lang.id)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  selectedLanguage === lang.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {lang.name}
              </button>
            ))}
          </div>

          <div className="h-80 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <ErrorBoundary fallback={
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>Error loading code editor. Please try again.</p>
              </div>
            }>
              <Editor
                height="100%"
                language={
                  selectedLanguage === 'python' ? 'python' : 
                  selectedLanguage === 'php' ? 'php' :
                  selectedLanguage === 'flutter' ? 'dart' :
                  selectedLanguage === 'kotlin' ? 'kotlin' :
                  selectedLanguage === 'swift' ? 'swift' : 'javascript'
                }
                theme={isDark ? "vs-dark" : "light"}
                value={code}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  readOnly: true,
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  folding: true,
                  bracketPairColorization: { enabled: true }
                }}
              />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    )
  }

  // Reset loading state when endpoint changes or docs tab is selected
  useEffect(() => {
    if (activeTab === 'docs') {
      setIsDocsLoading(true)
    }
  }, [selectedEndpoint, activeTab])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Documentation & Tools
        </h2>
        
        {/* Tabs */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-1">
          <button
            onClick={() => setActiveTab('docs')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeTab === 'docs'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Book className="w-4 h-4 inline mr-1" />
            Docs
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeTab === 'code'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Code className="w-4 h-4 inline mr-1" />
            Code & cURL
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-hidden ${activeTab === 'docs' ? 'p-2' : 'p-4 overflow-y-auto'}`}>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={activeTab === 'docs' ? 'h-full' : ''}
        >
          {activeTab === 'docs' && renderDocumentation()}
          {activeTab === 'code' && renderCodeAndCurl()}
        </motion.div>
      </div>
    </div>
  )
}

export default DocumentationPanel
